import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

const STAGES = ['applied', 'screening', 'interview', 'offer', 'hired', 'rejected'];
const STAGE_COLORS = { applied: 'bg-gray-100 text-gray-700', screening: 'bg-yellow-50 text-yellow-700', interview: 'bg-blue-50 text-blue-700', offer: 'bg-purple-50 text-purple-700', hired: 'bg-green-50 text-green-700', rejected: 'bg-red-50 text-red-700' };

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState('');

  const load = () => {
    api(`/ats/jobs/${id}`).then(r => r.ok ? r.json() : null).then(setJob).catch(() => {});
    api(`/ats/applicants?jobId=${id}`).then(r => r.ok ? r.json() : []).then(setApplicants).catch(() => {});
    api(`/ats/jobs/${id}/members`).then(r => r.ok ? r.json() : []).then(setMembers).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const moveStage = async (applicantId, stage) => {
    await api(`/ats/applicants/${applicantId}`, { method: 'PUT', body: JSON.stringify({ stage }) });
    load();
  };

  if (!job) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/ats/jobs" className="text-teal-600 hover:text-teal-800 text-sm">← Back to Jobs</Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{job.title}</h2>
            <div className="flex gap-4 mt-2 text-sm text-gray-500">
              {job.department && <span>{job.department}</span>}
              {job.location && <span>{job.location}</span>}
              {job.employmentType && <span>{job.employmentType.replace('_', ' ')}</span>}
              {(job.salaryMin || job.salaryMax) && <span>{job.salaryMin ? `$${Number(job.salaryMin).toLocaleString()}` : ''}{job.salaryMin && job.salaryMax ? ' – ' : ''}{job.salaryMax ? `$${Number(job.salaryMax).toLocaleString()}` : ''}</span>}
            </div>
          </div>
          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${job.status === 'open' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{job.status}</span>
        </div>
        {job.description && <div className="prose prose-gray max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: job.description }} />}
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Hiring Team</h3>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {members.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm">
              {m.userName || m.userId}
              <button onClick={async () => { await api(`/ats/job-members/${m.id}`, {method: 'DELETE'}); load(); }} className="hover:text-red-500">&times;</button>
            </span>
          ))}
          {members.length === 0 && <span className="text-sm text-gray-500">No team members assigned.</span>}
        </div>
        <form onSubmit={async (e) => { e.preventDefault(); if (!newMember.trim()) return; await api(`/ats/jobs/${id}/members`, {method: 'POST', body: JSON.stringify({userId: newMember.trim(), userName: newMember.trim()})}); setNewMember(''); load(); }} className="flex gap-2">
          <input value={newMember} onChange={e => setNewMember(e.target.value)} placeholder="Add team member..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
          <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">Add</button>
        </form>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-4">Applicants ({applicants.length})</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {STAGES.map(stage => {
          const count = applicants.filter(a => a.stage === stage).length;
          return (
            <div key={stage} className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className={`text-xs font-medium mt-1 inline-flex px-2 py-0.5 rounded ${STAGE_COLORS[stage]}`}>{stage}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Move To</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {applicants.map(a => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <Link to={`/ats/applicants/${a.id}`} className="text-teal-600 hover:text-teal-800 font-medium">{a.firstName} {a.lastName}</Link>
                </td>
                <td className="px-6 py-4 text-gray-600 text-sm">{a.email || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${STAGE_COLORS[a.stage] || 'bg-gray-100 text-gray-600'}`}>{a.stage}</span>
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm">{a.appliedAt ? new Date(a.appliedAt).toLocaleDateString() : '—'}</td>
                <td className="px-6 py-4 text-right">
                  <select value="" onChange={e => { if (e.target.value) moveStage(a.id, e.target.value); }} className="text-sm border border-gray-300 rounded px-2 py-1">
                    <option value="">Move to...</option>
                    {STAGES.filter(s => s !== a.stage).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {applicants.length === 0 && <div className="text-center py-12 text-gray-500">No applicants for this position.</div>}
      </div>
    </Layout>
  );
}
